import React, { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Images
import SlackLogo from 'Shared/images/slackLogo.svg';

// Hooks
import { useWorkspaceAgendaAi } from 'Shared/utils/hooks/use-workspace-agenda-ai';
import { useToggle, useQueryParam } from 'Shared/utils/hooks';
import OAuthButton from 'Shared/components/OAuthButton/OAuthButton';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Components
import { WpButton } from 'Wpds/components/button';
import { WpBadgeList, WpBadge } from 'Wpds/components/badge';
import { WpContainer, WpContainerBody, WpContainerHeader } from 'Wpds/components/container';
import WpIcon from 'Wpds/components/icon/WpIcon';
import Switcher from 'Dashboard/Switcher/Switcher';
import WorkspaceAgendaAiSettingsModal from 'Shared/components/WorkspaceAgendaAiSettingsModal/WorkspaceAgendaAiSettingsModal';

// Styles
import './WorkspaceDetailsAgendaAI.scss';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

type Props = {
  toggleSettingsModal: Function;
};

const WorkspaceDetailsAgendaAI = ({ toggleSettingsModal }: Props) => {
  const { featureFlagEnablePiSuggested } = useFlags();
  const [showModalOnLoad] = useQueryParam('show');

  const {
    slackAiOrganizationDataSourceId,
    isFetching,
    selectedChannels,
    isSlackAiActive,
    handleUpdateWorkspaceAiSetting,
  } = useWorkspaceAgendaAi();

  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const { id: workspaceId } = useWorkspace();

  const OAuthRedictUrl = `${PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId)}?show=agenda_ai`;

  const handleStatusOnChange = () => {
    handleUpdateWorkspaceAiSetting({ active: !isSlackAiActive });
  };

  useEffect(() => {
    if (showModalOnLoad === 'agenda_ai') toggleIsModalOpen();
  }, []);

  const baseClassName = 'WorkspaceDetailsAgendaAI';
  return (
    <>
      <WpContainer className={baseClassName}>
        <WpContainerHeader>
          <div className={`${baseClassName}__header`}>
            <div className={`${baseClassName}__header__name-wrapper`}>
              <img src={SlackLogo} className={`${baseClassName}__header__name-wrapper__integration-logo`} />
              Slack
            </div>
            {!featureFlagEnablePiSuggested &&
              slackAiOrganizationDataSourceId &&
              !isFetching &&
              selectedChannels &&
              selectedChannels?.length > 0 && (
                <div className={`${baseClassName}__header__switcher`}>
                  <Switcher
                    id="workspace-agenda-switcher"
                    checked={isSlackAiActive}
                    onChange={handleStatusOnChange}
                    isOneSideLabel
                  />
                </div>
              )}
          </div>
        </WpContainerHeader>
        <WpContainerBody>
          {!slackAiOrganizationDataSourceId ? (
            <div>
              <p>Connect Slack to get recommendations</p>
              <OAuthButton
                oauthProvider="slack"
                text="Connect Slack"
                block={false}
                size="md"
                color="primary"
                iconType="icon"
                redirectUrl={OAuthRedictUrl}
              />
            </div>
          ) : (
            <>
              {isFetching ? (
                <>Fetching channels...</>
              ) : (
                <>
                  {selectedChannels && selectedChannels?.length > 0 ? (
                    <>
                      <p>Selected channels to find recommend topics</p>
                      <WpBadgeList>
                        {selectedChannels?.map(item => (
                          <WpBadge color="blue" key={item.id} size="lg">
                            <WpIcon name={item.is_private ? 'lock-alt' : 'hashtag'} size="sm" />
                            {item.name}
                          </WpBadge>
                        ))}
                      </WpBadgeList>
                    </>
                  ) : (
                    <>Select Slack channels to generate topics</>
                  )}
                  <div className={`${baseClassName}__edit-btn`}>
                    <WpButton variant="secondary" onClick={toggleIsModalOpen}>
                      Edit channels
                    </WpButton>
                  </div>
                </>
              )}
            </>
          )}
        </WpContainerBody>
      </WpContainer>
      {isModalOpen && (
        <WorkspaceAgendaAiSettingsModal
          isOpen={isModalOpen}
          toggleIsOpen={toggleIsModalOpen}
          toggleSettingsModal={toggleSettingsModal}
        />
      )}
    </>
  );
};

export default WorkspaceDetailsAgendaAI;
